import React, { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { useParams } from "react-router-dom";
import { auth, db } from "../services/firebase";
import { logUiEvent } from "../services/analytics";
import { buildWebApp } from "../services/functionsClient";

const apiRows = [
  { name: "generatePlan", method: "POST", path: "/api/generatePlan", auth: "google" },
  { name: "dispatchAppApi", method: "POST", path: "/api/dispatch", auth: "google" },
  { name: "updateCanvas", method: "POST", path: "/api/canvas", auth: "email" }
];

function normalizeJson(text = "") {
  const trimmed = text.trim();
  const fenceMatch = trimmed.match(/```(?:json)?\n([\s\S]*?)```/i);
  return fenceMatch ? fenceMatch[1].trim() : trimmed;
}

function generateIframeContent(code) {
  if (!code) return "";

  const html = code.html || "";
  const css = code.css || "";
  const js = code.js || "";
  const apiBaseUrl = (process.env.REACT_APP_FUNCTION_BASE_URL || "").replace(/"/g, "\\\"");

  return `<!doctype html>
<html>
  <head>
    <style>${css}</style>
  </head>
  <body>
    ${html}
    <script>const API_BASE_URL = "${apiBaseUrl}";</script>
    <script type="module">
      try {
        ${js}
      } catch (e) {
        console.error("Error executing generated app:", e);
        const errorDiv = document.createElement('div');
        errorDiv.style.position = 'fixed';
        errorDiv.style.bottom = '10px';
        errorDiv.style.left = '10px';
        errorDiv.style.padding = '10px';
        errorDiv.style.backgroundColor = 'red';
        errorDiv.style.color = 'white';
        errorDiv.style.zIndex = '9999';
        errorDiv.textContent = 'Error in script: ' + e.message;
        document.body.appendChild(errorDiv);
      }
    </script>
  </body>
</html>`;
}

function getMessageContent(message) {
  const content = message?.content ?? message;

  if (typeof content === "string") return content;
  if (typeof content === "object" && content !== null) {
    if (typeof content.content === "string") return content.content;
    if (typeof content.prompt === "string") return content.prompt;
    if (typeof content.message === "string") return content.message;

    try {
      return JSON.stringify(content, null, 2);
    } catch (err) {
      console.warn("Failed to stringify message content", err);
    }
  }

  return "";
}

function parseRevisionContent(content) {
  if (!content) return null;

  if (typeof content === "object" && content !== null && !Array.isArray(content)) {
    if (content.html || content.css || content.js) return content;
    if (content.code) return parseRevisionContent(content.code);
  }

  const normalized = normalizeJson(typeof content === "string" ? content : JSON.stringify(content || ""));
  if (!normalized) return null;

  try {
    const parsed = JSON.parse(normalized);
    if (parsed && typeof parsed === "object") {
      if (parsed.html || parsed.css || parsed.js) return parsed;
      if (parsed.code && typeof parsed.code === "object") {
        return {
          html: parsed.code.html || parsed.html || "",
          css: parsed.code.css || parsed.css || "",
          js: parsed.code.js || parsed.js || "",
        };
      }
    }
  } catch (err) {
    console.warn("Failed to parse revision content", err);
  }

  return null;
}

function EditorPage() {
  const { appId } = useParams();
  const [appData, setAppData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [iframeSrcDoc, setIframeSrcDoc] = useState("");
  const [iframeError, setIframeError] = useState("");
  const [selectedRevisionId, setSelectedRevisionId] = useState(null);
  const [hasUserSelectedRevision, setHasUserSelectedRevision] = useState(false);
  const [changeRequest, setChangeRequest] = useState("");
  const [submittingChange, setSubmittingChange] = useState(false);
  const [changeStatus, setChangeStatus] = useState("");
  const [changeError, setChangeError] = useState("");

  const userId = auth.currentUser?.uid;

  const { chatMessages, revisions } = useMemo(() => {
    const result = { chatMessages: [], revisions: [] };

    if (!appData?.code || !Array.isArray(appData.code)) return result;

    let revisionCounter = 1;

    appData.code.forEach((message, index) => {
      if (index === 0) return;

      const textContent = getMessageContent(message);
      const parsedRevision =
        message.role === "assistant" && parseRevisionContent(textContent);

      if (parsedRevision) {
        const labelFromMessage =
          message.slug || message.title || message.name || message?.content?.slug;
        const label = labelFromMessage
          ? `Revision: ${labelFromMessage}`
          : `Revision ${revisionCounter}`;
        const revisionId = message.id || `chat-${index}`;
        revisionCounter += 1;

        result.revisions.push({
          id: revisionId,
          label,
          code: parsedRevision,
          rawContent: textContent,
        });

        result.chatMessages.push({
          key: `chat-${index}`,
          role: "assistant",
          text: `${label} posted`,
          isRevision: true,
        });
      } else {
        result.chatMessages.push({
          key: `chat-${index}`,
          role: message.role === "assistant" ? "assistant" : "user",
          text: textContent || "",
          isRevision: false,
        });
      }
    });

    if (appData.webui_code) {
      const parsed = parseRevisionContent(appData.webui_code);
      if (parsed) {
        const savedLabel = appData.webui_code_updatedAt
          ? `Saved revision (${new Date(appData.webui_code_updatedAt).toLocaleString()})`
          : "Saved revision";
        const alreadyPresent = result.revisions.some(
          (rev) => rev.rawContent === appData.webui_code
        );

        if (!alreadyPresent) {
          result.revisions.push({
            id: "webui_code",
            label: savedLabel,
            code: parsed,
            rawContent: appData.webui_code,
          });
        }
      }
    }

    return result;
  }, [appData]);

  useEffect(() => {
    if (!userId || !appId) return undefined;

    const appRef = doc(db, "users", userId, "apps", appId);
    const unsubscribe = onSnapshot(
      appRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setError("App not found. Try generating a new app from the dashboard.");
          setLoading(false);
          return;
        }

        const data = { id: snapshot.id, ...snapshot.data() };
        setAppData(data);
        setLoading(false);
      },
      (snapshotError) => {
        setError(snapshotError.message || "Failed to load app data.");
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [appId, userId]);

  useEffect(() => {
    if (!revisions.length) {
      setSelectedRevisionId(null);
      setIframeSrcDoc("");
      setIframeError("No generated UI code yet. Request a build in Build Web UI Dev.");
      setHasUserSelectedRevision(false);
      return;
    }

    const latestRevision = revisions[revisions.length - 1];
    const alreadySelected = revisions.some((rev) => rev.id === selectedRevisionId);
    if (!alreadySelected || !hasUserSelectedRevision) {
      setSelectedRevisionId(latestRevision.id);
    }
  }, [hasUserSelectedRevision, revisions, selectedRevisionId]);

  useEffect(() => {
    if (!selectedRevisionId) return;

    const revision = revisions.find((rev) => rev.id === selectedRevisionId);
    if (!revision) {
      setIframeSrcDoc("");
      setIframeError("Selected revision is unavailable.");
      return;
    }

    try {
      setIframeSrcDoc(generateIframeContent(revision.code));
      setIframeError("");
    } catch (parseError) {
      setIframeSrcDoc("");
      setIframeError(parseError.message || "Failed to parse generated code.");
    }
  }, [revisions, selectedRevisionId]);

  if (loading) {
    return <div className="splash">Loading app experience...</div>;
  }

  if (error) {
    return <div className="splash">{error}</div>;
  }

  const handleSubmitChangeRequest = async (event) => {
    event?.preventDefault();

    if (!changeRequest.trim()) {
      setChangeError("Enter a change request first.");
      return;
    }

    if (!userId || !appId) {
      setChangeError("You must be signed in to request changes.");
      return;
    }

    setSubmittingChange(true);
    setChangeError("");
    setChangeStatus("Submitting change request to the builder agent...");

    try {
      await buildWebApp({
        userId,
        appId,
        userPrompt: changeRequest.trim(),
        model: appData?.model,
        reasoningEffort: appData?.reasoningEffort,
      });

      setChangeStatus("Change request sent. Waiting for the new revision to appear...");
      logUiEvent("editor_change_request_submitted", { appId });
      setChangeRequest("");
    } catch (requestError) {
      setChangeError(requestError.message || "Failed to submit change request.");
      setChangeStatus("");
    } finally {
      setSubmittingChange(false);
    }
  };

  return (
    <div className="grid" style={{ gap: "14px" }}>
      <div className="section-header">
        <div>
          <h2 className="section-header__title">Editing {appData?.name || appId}</h2>
          <p className="muted">Agents and UI changes sync to Firebase Functions and Hosting.</p>
        </div>
        <div className="flex-center">
          <button className="button" onClick={() => logUiEvent("deploy_webapp", { appId })}>
            Deploy webapp
          </button>
          <button className="button secondary" onClick={() => logUiEvent("deploy_functions", { appId })}>
            Deploy functions
          </button>
        </div>
      </div>

      <div className="panel">
        <h3 className="section-title">Agent surface</h3>
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Method</th>
              <th>Path</th>
              <th>Auth</th>
            </tr>
          </thead>
          <tbody>
            {apiRows.map((row) => (
              <tr key={row.name}>
                <td>{row.name}</td>
                <td>{row.method}</td>
                <td>{row.path}</td>
                <td>{row.auth}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="muted">Agents are versioned in Firestore and served through a single dispatcher function.</p>
      </div>

      <div className="panel">
        <h3 className="section-title">Canvas layout</h3>
        <p className="muted">
          The canvas is generated from the agent's plan. Users can request edits via chat and changes will sync here.
        </p>
        <div className="canvas-layout">
          <div className="canvas-layout__chat">
            <div className="canvas-layout__header">
              <span className="badge">Canvas assistant</span>
              <span className="muted">Ask for UI tweaks, data wiring, or deployments.</span>
            </div>
            <div className="canvas-layout__conversation">
              {chatMessages.length === 0 && <p className="muted">No chat history yet.</p>}
              {chatMessages.map((message) => (
                <div
                  key={message.key}
                  className={`chat-message chat-message--${message.role === "assistant" ? "assistant" : "user"}`}
                >
                  {message.text ? (
                    message.isRevision ? (
                      <span className="muted">{message.text}</span>
                    ) : (
                      message.text
                    )
                  ) : (
                    <em className="muted">(empty message)</em>
                  )}
                </div>
              ))}
            </div>
            <form className="canvas-layout__input" onSubmit={handleSubmitChangeRequest}>
              <textarea
                className="textarea"
                placeholder="Describe the change you want to see in the app..."
                value={changeRequest}
                onChange={(event) => setChangeRequest(event.target.value)}
                disabled={submittingChange}
              />
              <button className="button" type="submit" disabled={submittingChange}>
                {submittingChange ? "Submitting..." : "Submit change request"}
              </button>
              {(changeStatus || changeError) && (
                <p className={changeError ? "error" : "muted"} style={{ marginTop: "8px" }}>
                  {changeError || changeStatus}
                </p>
              )}
            </form>
          </div>
          <div className="canvas-layout__preview">
            <div className="canvas-layout__header">
              <span className="badge">Rendered webapp</span>
              <span className="muted">Live preview updates after each request.</span>
            </div>
            <div className="canvas-layout__revisions">
              {revisions.length === 0 ? (
                <p className="muted">No revisions yet.</p>
              ) : (
                revisions.map((revision) => (
                  <button
                    key={revision.id}
                    type="button"
                    className={`revision-chip ${revision.id === selectedRevisionId ? "active" : ""}`}
                    onClick={() => {
                      setHasUserSelectedRevision(true);
                      setSelectedRevisionId(revision.id);
                    }}
                  >
                    {revision.label}
                  </button>
                ))
              )}
            </div>
            <div className="canvas-layout__previewFrame">
              {iframeSrcDoc ? (
                <iframe
                  title="app-preview"
                  className="canvas-layout__iframe"
                  srcDoc={iframeSrcDoc}
                  sandbox="allow-scripts allow-same-origin"
                />
              ) : (
                <div className="muted" style={{ padding: "16px" }}>
                  {iframeError || "No preview available yet."}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="panel">
        <h3 className="section-title">Exports & Sharing</h3>
        <div className="flex-center">
          <button className="button secondary">Copy share link</button>
          <button className="button secondary">Export zip</button>
        </div>
      </div>
    </div>
  );
}

export default EditorPage;
